import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, CheckCircle2, Clock, ChevronRight, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CAT_INFO = {
  A: { name: "Behavioral Science Fundamentals", desc: "Fondasi ilmu perilaku dari tokoh-tokoh terkemuka", gradient: "from-indigo-500/20 to-indigo-600/5" },
  B: { name: "Behavioral Science dalam Tech", desc: "Bagaimana behavioral science digunakan di industri tech", gradient: "from-purple-500/20 to-purple-600/5" },
  C: { name: "Behavioral Science & AI", desc: "Perpaduan AI dan psikologi perilaku di era modern", gradient: "from-violet-500/20 to-violet-600/5" },
};

export default function KnowledgeBase() {
  const [lessons, setLessons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/lessons`),
      axios.get(`${API}/categories`)
    ]).then(([lessonsRes, catsRes]) => {
      setLessons(lessonsRes.data);
      setCategories(catsRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filteredLessons = selectedCat
    ? lessons.filter(l => l.category === selectedCat)
    : lessons;

  if (loading) return <PageLoader />;

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-xs text-indigo-400 font-mono mb-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          // 20 lesson · 3 kategori
        </p>
        <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-4 md:mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Knowledge Base
        </h1>
      </motion.div>

      {/* Category Cards */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 md:mb-8"
      >
        {categories.map((cat, i) => {
          const info = CAT_INFO[cat.id] || {};
          const pct = cat.total > 0 ? Math.round((cat.completed / cat.total) * 100) : 0;
          const isSelected = selectedCat === cat.id;
          return (
            <button
              key={cat.id}
              data-testid={`category-card-${cat.id}`}
              onClick={() => setSelectedCat(isSelected ? null : cat.id)}
              className={`text-left p-5 rounded-2xl border transition-colors duration-200 ${
                isSelected
                  ? "border-indigo-500/40 bg-indigo-500/10"
                  : "border-white/10 bg-[#121216] hover:border-white/20"
              }`}
            >
              <span className="text-xs font-mono text-indigo-400" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                Kategori {cat.id}
              </span>
              <h3 className="text-sm font-semibold text-white mt-1 mb-1 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {info.name}
              </h3>
              <p className="text-xs text-[#94A3B8] mb-3 leading-snug">{info.desc}</p>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-[#94A3B8]">{cat.completed}/{cat.total} selesai</span>
                <span className="text-xs text-indigo-400">{pct}%</span>
              </div>
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-700"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </button>
          );
        })}
      </motion.div>

      {/* Filter Pill */}
      {selectedCat && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-[#94A3B8]" style={{ fontFamily: "'Inter', sans-serif" }}>Filter:</span>
          <span className="text-xs bg-indigo-500/15 border border-indigo-500/25 text-indigo-300 px-3 py-1 rounded-full">
            Kategori {selectedCat}
          </span>
          <button
            onClick={() => setSelectedCat(null)}
            className="text-xs text-[#94A3B8] hover:text-white underline"
          >
            Hapus filter
          </button>
        </div>
      )}

      {/* Lessons List */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
        className="space-y-2"
      >
        <AnimatePresence mode="wait">
          {filteredLessons.map((lesson, i) => (
            <motion.button
              key={lesson.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              data-testid={`lesson-item-${lesson.id}`}
              onClick={() => navigate(`/lesson/${lesson.id}`)}
              className="w-full text-left flex items-center gap-4 bg-[#121216] hover:bg-[#1A1A20] border border-white/10 hover:border-white/20 rounded-xl px-5 py-4 transition-colors duration-200 group"
            >
              {/* Day Badge */}
              <div className="w-10 h-10 rounded-lg bg-white/5 flex-shrink-0 flex items-center justify-center">
                {lesson.completed ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <span className="text-xs font-mono text-[#94A3B8]">{lesson.day}</span>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] font-mono text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-1.5 py-0.5 rounded-full">
                    Kat. {lesson.category}
                  </span>
                  {lesson.completed && (
                    <span className="text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded-full">
                      Selesai
                    </span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-white group-hover:text-indigo-300 transition-colors duration-200 truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {lesson.title}
                </h3>
                <p className="text-xs text-[#94A3B8] truncate mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {lesson.intro}
                </p>
              </div>

              {/* Right info */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="flex items-center gap-1 text-xs text-[#94A3B8]">
                  <Clock className="w-3 h-3" />
                  {lesson.duration_minutes}m
                </span>
                <ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-indigo-400 transition-colors duration-200" />
              </div>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-[60vh]">
      <div className="w-6 h-6 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
    </div>
  );
}
